<script setup lang="ts">
import '@/utils/floorUtils.ts';
import { computed, onMounted, provide, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import HeaderWithBackButton from '@/components/utilities/HeaderWithBackButton.vue';
import UnitCanvasDialogModal from '@/components/dialogs/UnitCanvasDialogModal.vue';
import UnitsFilterModal from '@/components/dialogs/UnitsFiltersModal.vue';
import UnitSelectionWizardSteps from '@/components/forms/UnitSelectionWizardSteps.vue';
import BuildingFacadeCarousel from '@/components/purchase/BuildingFacadeCarousel.vue';
import SlidersSolid from '@/components/svg/SlidersSolid.vue';
import UnitDatatable from '@/components/tables/UnitDatatable.vue';
import UnitList from '@/components/rental/units/UnitList.vue';
import CanvasDataPanels from '@/components/utilities/CanvasDataPanels.vue';
import LoadingSpinner from '@/components/utilities/LoadingSpinner.vue';
import ViewModeToggleButton from '@/components/buttons/ViewModeToggleButton.vue';
import { useBuildingFacadesApi } from '@/composables/useBuildingFacadesApi';
import { useFloor } from '@/composables/useFloor.ts';
import { usePurchaseLocalizedUrls } from '@/composables/usePurchaseLocalizedUrls';
import { useUnit } from '@/composables/useUnit';
import { useUnitsApi } from '@/composables/useUnitsApi';
import type { AvailableLocales, LocaleSchema } from '@/locales/index';
import type { Building } from '@/typings/buildings/Building';
import type { BuildingFacade } from '@/typings/buildings/BuildingFacade';
import type { FacadeRenderLayer } from '@/typings/drawing/FacadeRenderLayer';
import type { GetUnitsRequest } from '@/typings/requests/GetUnitsRequest.ts';
import type { Unit } from '@/typings/units/Unit';
import { DefaultUnitsFilters, type UnitsFilters } from '@/typings/units/UnitsFilters';
import { ListingTypeEnum } from '@/enums/ListingType';
import { DEFAULT_PURCHASE_UNIT_STATUSES } from '@/constants/purchaseConstants';
import { UnitStatusEnum } from '@/enums/UnitStatus';
import { useCanvas } from '@/composables/useCanvas';
import { InlineSearchBar } from '@/components/utilities/search';
import { usePurchaseFilters } from '@/composables/usePurchaseFilters';

type ViewMode = 'building';

const { getBuildingFacades, getBuildingFacadesLoading } = useBuildingFacadesApi();
const { getUnitsByBuildingId, getUnitsByBuildingIdLoading } = useUnitsApi();
const { sortUnitsByStatus } = useUnit();
const { t, locale } = useI18n<{ message: LocaleSchema }, AvailableLocales>({
  useScope: 'global',
});

const { getProjectWizardUrlFromBuilding, getPurchaseUnitWizardUrl } = usePurchaseLocalizedUrls();
const { loadFiltersFromPurchaseStorage } = usePurchaseFilters();

const props = defineProps<{
  building: Building;
}>();

const INITIAL_UNITS_COUNT = 18;

const displayedUnitsCount = ref(INITIAL_UNITS_COUNT);

const buildingCanvasIsLoaded = ref(false);

const buildingId = ref<number>(props.building.id);
// Modal state
const showUnitModal = ref(false);
const modalUnit = ref<Unit>();
const buildingFacades = ref<BuildingFacade[]>([]);
const hoveredUnit = ref<Unit>();
const selectedFacade = ref<BuildingFacade>();
const units = ref<Unit[]>([]);

// Mode d'affichage (cards ou table) -- toujours 'table' par défaut
const displayViewMode = ref<'cards' | 'table'>('table');

// Détection de la largeur d'écran pour afficher le bon bouton
const isMobile = ref(window.innerWidth < 768);

const wizardStep = ref(4);
const unitsFilters = ref<UnitsFilters>({ ...DefaultUnitsFilters });
const unitsFilterModalOpen = ref(false);
const { filterUnits } = useUnit();
const { getFloorTextByNumber } = useFloor();
const { isNullOrEmpty } = useCanvas();

const filterCount = computed(() => {
  return Object.values(unitsFilters.value).reduce((acc: number, filter) => {
    if (Array.isArray(filter)) {
      return acc + filter.length;
    }
    
    return acc + (filter ? 1 : 0);
  }, 0);
});

const hasFilters = computed(() => {
  return filterCount.value > 0;
});

const unitCount = computed(() => {
  return filteredUnits.value?.length ?? units.value?.length ?? 0;
});

const buildingLocationTitle = computed(() => {
  return `${props.building.localizedProjectName}, ${props.building.localizedRegionName}`;
});

const buildingNumberTitle = computed(() => {
  return `${t('browsingApp.buildings.buildingNumber')} ${props.building.no}`;
});

const hasFilteredFloors = computed(() => {
  return unitsFilters.value.selectedFloorNo;
});

const openUnitsFilterModal = () => {
  unitsFilterModalOpen.value = true;
};

const loadUnits = async () => {
  if (buildingId.value) {
    try {
      const allFilters = loadFiltersFromPurchaseStorage();
      
      const unitRequest: GetUnitsRequest = {
        locale: locale.value?.toString().substring(0, 2) || 'fr',
        listingTypes: [ListingTypeEnum.Sale],
        statuses: DEFAULT_PURCHASE_UNIT_STATUSES,
        unitConfigurations: allFilters.unitConfigurations && allFilters.unitConfigurations.length > 0 ? allFilters.unitConfigurations : undefined,
        availabilityDates: allFilters.availabilityDates,
        floorNumbers: allFilters.floors,
        minPrice: allFilters.minPrice,
        maxPrice: allFilters.maxPrice
      };
      
      const unitsData = await getUnitsByBuildingId(buildingId.value, unitRequest);
      units.value = unitsData;

      // Réinitialiser le compteur d'unités affichées
      displayedUnitsCount.value = INITIAL_UNITS_COUNT;
    } catch (error) {
      console.error('Failed to reload units with filters:', error);
    }
  }
};

// Fonction pour synchroniser les filtres locaux avec ceux du localStorage
const syncFiltersFromStorage = () => {
  // Les unités sont déjà filtrées côté serveur par loadUnits(), 
  // donc on réinitialise les filtres locaux pour afficher toutes les unités reçues
  unitsFilters.value = { ...DefaultUnitsFilters };
};

// Fonction pour appliquer les filtres secondaires
const applySecondaryFilters = async () => {
  // Recharger les unités avec les nouveaux filtres
  await loadUnits();
  // Synchroniser les filtres locaux pour afficher les nouveaux résultats
  syncFiltersFromStorage();
};

function onBuildingCanvasLoaded() {
  buildingCanvasIsLoaded.value = true;
}

function onFacadeSelect(facade: BuildingFacade) {
  selectedFacade.value = facade;
}

function onUnitLayerClick(layer: any) {
  const facadeLayer = layer as FacadeRenderLayer;
  const unit = units.value.find(u => u.id === facadeLayer.unitId);
  if (unit) {
    modalUnit.value = unit;
    showUnitModal.value = true;
  }
}

function onUnitLayerHover(layer: any | undefined) {
  if (!layer) {
    hoveredUnit.value = undefined;
    return;
  }
  
  const facadeLayer = layer as FacadeRenderLayer;
  const unit = units.value.find(u => u.id === facadeLayer.unitId);
  if (unit) {
    hoveredUnit.value = unit;
  }
}

function onBackToBuildingSelection() {
  window.location.href = getProjectWizardUrlFromBuilding(props.building);
}

function onUnitSelected(unit: Unit) {
  // Navigation directe vers l'étape suivante
  window.location.href = getPurchaseUnitWizardUrl(unit);
}

onMounted(async () => {
  if (buildingId.value) {
    try {
      const unitRequest: GetUnitsRequest = {
        locale: locale.value.substring(0, 2),
        listingTypes: [ListingTypeEnum.Sale],
        statuses: DEFAULT_PURCHASE_UNIT_STATUSES
      };
      const [facadesData, unitsData] = await Promise.all([
        getBuildingFacades(buildingId.value),
        getUnitsByBuildingId(buildingId.value, unitRequest)
      ]);

      buildingFacades.value = facadesData;
      units.value = unitsData;
      
      // Sélectionner automatiquement la première façade avec des unités "Upcoming" ou "Available"
      const sortedFacades = [...buildingFacades.value].sort((a, b) => a.facadeOrder - b.facadeOrder);
      if (sortedFacades.length > 0) {
        const facadeWithAvailableUnits = sortedFacades.find(facade =>
          facade.facadeUnits.some(facadeUnit => {
            if (isNullOrEmpty(facadeUnit.canvasPolygonData)) {
              return false; // Ignorer les façades sans données de polygone. C'est possible d'avoir des façades avec une liste vide de polygones.
            }

            const unit = unitsData.find(u => u.id === facadeUnit.unitId);
            return unit && (unit.status === UnitStatusEnum.Available);
          })
        );
        
        // Si aucune façade avec des unités disponibles n'est trouvée, utiliser la première façade
        selectedFacade.value = facadeWithAvailableUnits || sortedFacades[0];
      }
    } catch (error) {
      console.error('Failed to load building data:', error);
    }
  }

  // Écouter les changements de taille d'écran
  const handleResize = () => {
    isMobile.value = window.innerWidth < 768;
  };
  window.addEventListener('resize', handleResize);
});

const filteredUnits = computed(() => filterUnits(units.value, unitsFilters.value));

provide('app-header-height', 120);
provide('filterCount', filterCount);
</script>

<template>
  <!--Achat - Étape 4 - Sélection de l'unité-->
  <div class="building-purchase-browsing">
    <!-- Barre de filtres -->
    <InlineSearchBar action-type="filter"
                     :listing-type="ListingTypeEnum.Sale"
                     :selected-region="props.building.regionId"
                     @filtersApplied="applySecondaryFilters" />
    <div class="wrapper wrapper_large">
      <UnitSelectionWizardSteps v-model="wizardStep" />
      <div class="main-content">
        <div class="heading-and-toggle">
          <!--En-tête avec bouton de retour-->
          <HeaderWithBackButton :title="`${buildingLocationTitle} — ${buildingNumberTitle}`"
                                :subtitle="t('browsingApp.units.chooseYourUnitFromBuildingDescription')"
                                @back-click="onBackToBuildingSelection">
            <template v-if="!isMobile" #actions>
              <!-- Toggle pour changer le mode d'affichage -->
              <ViewModeToggleButton v-model="displayViewMode" />
            </template>
          </HeaderWithBackButton>
        </div>
        <CanvasDataPanels>
          <template v-if="!getBuildingFacadesLoading"
                    #canvas>
            <BuildingFacadeCarousel v-model:hovered-unit="hoveredUnit"
                                    :building-facades="buildingFacades"
                                    :units="units"
                                    :selected-facade="selectedFacade"
                                    @facade-select="onFacadeSelect"
                                    @unit-layer-click="onUnitLayerClick"
                                    @unit-layer-hover="onUnitLayerHover"
                                    @canvas-loaded="onBuildingCanvasLoaded" />
          </template>
          <template v-if="!getUnitsByBuildingIdLoading"
                    #data>
            <template v-if="units?.length">
              <div class="browsingApp-datatableActions">
                <div class="browsingApp-datatableControls">
                  <div class="browsingApp-datatableUnitCount">
                    {{ t('browsingApp.units.unitCount', { count: unitCount }, unitCount) }}
                  </div>

                  <button class="btn btn_secondary"
                          @click="openUnitsFilterModal">
                    <SlidersSolid class="icon" />
                    <span>{{ t('browsingApp.units.filters') }}</span>
                    <span v-if="hasFilters"
                          class="countBadge">{{ filterCount }}</span>
                  </button>
                </div>
                <UnitsFilterModal v-model:open="unitsFilterModalOpen"
                                  v-model:filters="unitsFilters"
                                  :all-units="units ?? []"
                                  :result-count="unitCount" />
              </div>

              <!-- Mode cartes -->
              <UnitList v-if="displayViewMode === 'cards' && filteredUnits?.length"
                        mode="purchase"
                        v-model:hovered="hoveredUnit"
                        :units="sortUnitsByStatus(filteredUnits)"
                        :loading="getUnitsByBuildingIdLoading"
                        :is-selectable="true"
                        @unit-selected="onUnitSelected" />

              <!-- Mode tableau -->
              <UnitDatatable v-else-if="displayViewMode === 'table' && filteredUnits?.length"
                             v-model:hovered="hoveredUnit"
                             mode="purchase"
                             :units="sortUnitsByStatus(filteredUnits)"
                             @unit-click="onUnitSelected" />

              <div v-else-if="!getBuildingFacadesLoading"
                   class="placeholderNoInformation">
                {{ t('browsingApp.units.noUnitsAvailable') }}
              </div>

              <!-- Toggle pour changer le mode d'affichage (mobile) -->
              <ViewModeToggleButton v-if="isMobile"
                                    v-model="displayViewMode" />
            </template>
            <template v-else>
              <div class="placeholderNoInformation">
                {{ t('browsingApp.units.noUnitsAvailable') }}
              </div>
            </template>
          </template>
        </CanvasDataPanels>

        <UnitCanvasDialogModal v-model:open="showUnitModal"
                               :unit="modalUnit"
                               :building="props.building"
                               @unit-selected="onUnitSelected"
                               @reset-unit-modal="modalUnit = undefined" />
      </div>
    </div>
  </div>
</template>

<style lang="scss">
.building-purchase-browsing{
  .placeholderNoInformation{
    margin-top:1.6rem;
  }
}
.browsingApp-datatableActions{
  padding-bottom:1.6rem;
}

@media screen and (max-width: 1024px){
 .browsingApp-datatableActions{
    padding-bottom:0;
  }
}

:root {
  --slide-distance: 30px;
  --slide-duration: 0.5s;
}

.slide-forward-enter-active,
.slide-forward-leave-active,
.slide-backward-enter-active,
.slide-backward-leave-active {
  transition: translate var(--slide-duration) ease, opacity var(--slide-duration) ease;
}

.slide-forward-enter-from {
  opacity: 0;
  translate: var(--slide-distance);
}

.slide-forward-leave-to {
  opacity: 0;
  translate: calc(var(--slide-distance) * -1);
}

.slide-backward-enter-from {
  opacity: 0;
  translate: calc(var(--slide-distance) * -1);
}

.slide-backward-leave-to {
  opacity: 0;
  translate: var(--slide-distance);
}

.slide-enter-active,
.slide-leave-active {
  transition: translate var(--slide-duration) ease, opacity var(--slide-duration) ease;
}

.slide-enter-from {
  opacity: 0;
  translate: var(--slide-distance);
}

.slide-leave-to {
  opacity: 0;
  translate: calc(var(--slide-distance) * -1);
}


/* Render canvas styles for facades */
.renderCanvas_facade {
  --baseColor: var(--gray-100);
}

.renderCanvas_facade [data-unit-status="Available"] {
  --baseColor: var(--clrGreenStatus);
}

.renderCanvas_facade [data-unit-status="Reserved"] {
  --baseColor: var(--clrOrangeStatus);
}

.renderCanvas_facade [data-unit-status="Sold"] {
  --baseColor: var(--clrRedStatus);
}

.renderCanvas_facade [data-unit-status="Rented"] {
  --baseColor: var(--clrRedStatus);
}

.renderCanvas_facade [data-unit-status="Upcoming"] {
  --baseColor: var(--clrYellowStatus);
}
</style>